import { useMemo, useRef, useState } from "react";
import { api, type User } from "../api";
import { go, upsertRoom, useStore } from "../store";
import { Avatar, Ini, toast, useMe, Veil } from "./ui";

/** ⌘K: jump to a chat, start one with one or more people, or search everything. */
export function NewMessage({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const users = useStore((s) => s.users);
  const rooms = useStore((s) => s.rooms);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [picked, setPicked] = useState<number[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const options = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const people = Object.values(users).filter((u) => u.active && u.id !== me.id && !picked.includes(u.id))
      .map((u) => ({ key: `u${u.id}`, label: u.name, sub: u.status || "Direct message", user: u as User | undefined, act: () => (picked.length ? setPicked((p) => [...p, u.id]) : openChat([u.id])), keep: picked.length > 0 }));
    const groups = picked.length ? [] : Object.values(rooms).filter((r) => r.kind === "group").map((r) => ({ key: `r${r.id}`, label: `#${r.name}`, sub: "Group", user: undefined, act: () => go({ v: "room", id: r.id }), keep: false }));
    const list = [...people, ...groups].filter((o) => !ql || o.label.toLowerCase().includes(ql));
    if (ql && !picked.length) list.push({ key: "search", label: `Search for “${q.trim()}”`, sub: "Messages, tasks, files", user: undefined, act: () => go({ v: "search", q: q.trim() }), keep: false });
    return list;
  }, [q, users, rooms, me.id, picked]);

  const openChat = async (ids: number[]) => {
    try { const r = await api.chat(ids); upsertRoom(r); go({ v: "room", id: r.id }); } catch (e: any) { toast(e.message || "Couldn't open the chat"); }
  };
  const pick = (i: number) => { const o = options[i]; if (!o) return; o.act(); if (o.keep) { setQ(""); setSel(0); input.current?.focus(); } else onClose(); };

  return (
    <Veil onClose={onClose}>
      <div className="dlg">
        <div className="f">
          <div className="to">
            {picked.map((id) => <span key={id} className="chip"><Ini user={users[id]} />{users[id]?.name}<button onClick={() => setPicked((p) => p.filter((x) => x !== id))}>×</button></span>)}
            <input ref={input} type="text" autoFocus placeholder={picked.length ? "Add someone else" : "Jump to a chat, or search"} value={q}
              onChange={(e) => { setQ(e.target.value); setSel(0); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, options.length - 1)); }
                if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
                if (e.key === "Enter") { if (picked.length && !q) { openChat(picked); onClose(); } else pick(sel); }
                if (e.key === "Backspace" && !q && picked.length) setPicked((p) => p.slice(0, -1));
              }} />
          </div>
        </div>
        <div className="pick">
          {options.map((o, i) => (
            <div key={o.key} className={`prow${i === sel ? " sel" : ""}`} onMouseEnter={() => setSel(i)}>
              <button className="pm" onClick={() => pick(i)}>
                {o.user ? <Avatar user={o.user} size={22} dot /> : <span className="gh">{o.key === "search" ? "🔍" : "#"}</span>}{o.label.replace(/^#/, "")}<small>{o.sub}</small>
              </button>
              {o.user && !picked.length && <button className="add" title="Add to a group chat" onClick={() => { setPicked([o.user!.id]); setQ(""); input.current?.focus(); }}>+</button>}
            </div>
          ))}
          {!options.length && <span className="nothing">Nobody by that name.</span>}
        </div>
        {picked.length > 0 && <div className="acts"><button className="btn pri" onClick={() => { openChat(picked); onClose(); }}>Start chat with {picked.map((id) => users[id]?.name).join(", ")}</button></div>}
      </div>
    </Veil>
  );
}

function PeoplePicker({ chosen, setChosen, exclude }: { chosen: number[]; setChosen: (f: (c: number[]) => number[]) => void; exclude?: number }) {
  const users = useStore((s) => s.users);
  return (
    <div className="chips">
      {Object.values(users).filter((u) => u.active && u.id !== exclude).map((u) => (
        <button key={u.id} className={chosen.includes(u.id) ? "on" : ""} onClick={() => setChosen((c) => (c.includes(u.id) ? c.filter((x) => x !== u.id) : [...c, u.id]))}>
          <Ini user={u} />{u.name}
        </button>
      ))}
    </div>
  );
}

export function NewGroup({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState<number[]>([]);
  const [err, setErr] = useState("");
  const make = async () => {
    try { const r = await api.group(name, chosen); upsertRoom(r); go({ v: "room", id: r.id }); onClose(); }
    catch (e: any) { setErr(e.message || "Couldn't make the group"); }
  };
  return (
    <Veil onClose={onClose}>
      <div className="dlg">
        <h3>New group</h3>
        {err && <div className="err">{err}</div>}
        <div className="f"><label>Name</label><input type="text" autoFocus placeholder="e.g. ops" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && make()} /></div>
        <div className="f"><label>Who's in it (you always are)</label><PeoplePicker chosen={chosen} setChosen={setChosen} exclude={me.id} /></div>
        <div className="acts"><button className="btn" onClick={onClose}>Cancel</button><button className="btn pri" disabled={!name.trim()} onClick={make}>Make group</button></div>
      </div>
    </Veil>
  );
}

export function Members({ roomId, onClose }: { roomId: number; onClose: () => void }) {
  const me = useMe();
  const room = useStore((s) => s.rooms[roomId]);
  const [name, setName] = useState(room?.name ?? "");
  const [chosen, setChosen] = useState<number[]>(room?.members.filter((u) => u !== me.id) ?? []);
  const [err, setErr] = useState("");
  if (!room) return null;
  const save = async () => {
    const before = room.members.filter((u) => u !== me.id);
    try {
      const r = await api.editGroup(roomId, { name: name !== room.name ? name : undefined, add: chosen.filter((u) => !before.includes(u)), remove: before.filter((u) => !chosen.includes(u)) });
      upsertRoom(r); onClose();
    } catch (e: any) { setErr(e.message || "Couldn't save"); }
  };
  return (
    <Veil onClose={onClose}>
      <div className="dlg">
        <h3>#{room.name}</h3>
        {err && <div className="err">{err}</div>}
        <div className="f"><label>Name</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="f"><label>Who's in it</label><PeoplePicker chosen={chosen} setChosen={setChosen} exclude={me.id} /></div>
        <div className="acts"><button className="btn" onClick={onClose}>Cancel</button><button className="btn pri" onClick={save}>Save</button></div>
      </div>
    </Veil>
  );
}

const COLORS = ["#04ac79", "#4f94e8", "#b07ce9", "#e65e73", "#07a4ba", "#b88a05", "#e8912d"];

/** Karol adds and removes people. They sign in with their rockenue.com Google account. */
export function People({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const users = useStore((s) => s.users);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const all = Object.values(users).sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
  const add = async () => {
    try {
      const u = await api.addUser({ name, email, color: COLORS[all.length % COLORS.length] });
      useStore.setState((s) => ({ users: { ...s.users, [u.id]: u } }));
      setName(""); setEmail(""); setErr("");
    } catch (e: any) { setErr(e.message || "Couldn't add"); }
  };
  const [sure, setSure] = useState<number | null>(null);
  const toggle = async (u: User) => {
    if (u.active && sure !== u.id) return setSure(u.id);
    setSure(null);
    try { const r = await api.editUser(u.id, { active: !u.active }); useStore.setState((s) => ({ users: { ...s.users, [r.id]: r } })); }
    catch (e: any) { toast(e.message || "Couldn't change"); }
  };
  return (
    <Veil onClose={onClose}>
      <div className="dlg">
        <h3>People</h3>
        <div className="pick" style={{ marginBottom: 14 }}>
          {all.map((u) => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", opacity: u.active ? 1 : 0.5 }}>
              <Ini user={u} />{u.name}<span style={{ color: "var(--ink3)", fontSize: 12 }}>{u.email}</span>
              {u.id !== me.id && <button className="btn" style={{ marginLeft: "auto", padding: "2px 10px", fontSize: 12 }} onClick={() => toggle(u)}>{u.active ? (sure === u.id ? "Sure? Click again" : "Remove") : "Bring back"}</button>}
            </div>
          ))}
        </div>
        {err && <div className="err">{err}</div>}
        <div className="f"><label>Add someone</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <input type="email" placeholder="name@rockenue.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          </div>
        </div>
        <div className="acts"><button className="btn" onClick={onClose}>Done</button><button className="btn pri" disabled={!name.trim() || !email.trim()} onClick={add}>Add</button></div>
      </div>
    </Veil>
  );
}
